import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useEffect, useSyncExternalStore } from 'react';
import { io, type Socket } from 'socket.io-client';
import { tokenStore } from './api';

/**
 * Live updates over Socket.io. The server only pushes small "something changed" events; the apps then
 * refetch through the normal, authorised REST endpoints. Polling stays in place as a fallback: it just
 * slows down while the socket is connected (see useVisibleInterval).
 */
let connectedCount = 0;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());

const subscribe = (cb: () => void) => {
  listeners.add(cb);
  return () => listeners.delete(cb);
};

/** True while at least one realtime connection is up. */
export function useRealtimeConnected(): boolean {
  return useSyncExternalStore(subscribe, () => connectedCount > 0, () => false);
}

type Auth = { token: string } | { track: { code: string; t?: string; p?: string } };

function open(auth: Auth, onEvent: (event: string, payload: unknown) => void): () => void {
  const socket: Socket = io({ auth, transports: ['websocket', 'polling'], reconnectionDelayMax: 10_000 });
  let up = false;
  const mark = (v: boolean) => {
    if (v === up) return;
    up = v;
    connectedCount += v ? 1 : -1;
    emit();
  };
  socket.on('ready', () => mark(true));
  socket.on('disconnect', () => mark(false));
  socket.on('connect_error', () => mark(false));
  socket.on('auth.error', () => socket.disconnect());
  for (const e of ['order.updated', 'rider.location', 'offer.new', 'offer.closed']) socket.on(e, (p) => onEvent(e, p));
  // after a reconnect we may have missed events: refresh everything once
  socket.io.on('reconnect', () => onEvent('resync', null));
  return () => {
    socket.close();
    mark(false);
  };
}

/** Keys that hold live operational data (settings, audit and messaging config are not refreshed by pushes). */
const STATIC = new Set(['settings', 'messaging', 'messaging-log', 'audit', 'sms-balance', 'merchant-sms-balances']);
const live = (key: readonly unknown[], roots: string[]) => roots.includes(String(key[0])) && !STATIC.has(String(key[1]));

function throttled(fn: () => void, ms: number): () => void {
  let last = 0;
  let timer: number | undefined;
  return () => {
    const wait = last + ms - Date.now();
    if (wait <= 0) {
      last = Date.now();
      fn();
    } else if (timer === undefined) {
      timer = window.setTimeout(() => {
        timer = undefined;
        last = Date.now();
        fn();
      }, wait);
    }
  };
}

/** Signed-in apps: refresh the right screens when orders, offers or rider positions change. */
export function useAccountRealtime(enabled: boolean, subscriptionKey: string): void {
  const qc = useQueryClient();
  useEffect(() => {
    const token = tokenStore.get();
    if (!enabled || !token) return;
    const refresh = throttled(() => void qc.invalidateQueries({ predicate: (q) => live(q.queryKey, ['merchant', 'admin', 'rider']) }), 700);
    const map = throttled(() => void qc.invalidateQueries({ predicate: (q) => q.queryKey[0] === 'admin' && (q.queryKey[1] === 'live' || q.queryKey[1] === 'monitor') }), 1500);
    return open({ token }, (event) => {
      if (event === 'rider.location') map();
      else refresh();
    });
  }, [enabled, qc, subscriptionKey]);
}

/** The public tracking page: refresh when its own order changes or the rider moves. */
export function useTrackingRealtime(code: string, auth: { t?: string; p?: string }, qc: QueryClient, enabled = true): void {
  useEffect(() => {
    if (!enabled || !code || (!auth.t && !auth.p)) return;
    const refresh = throttled(() => void qc.invalidateQueries({ queryKey: ['track', code] }), 1000);
    return open({ track: { code, t: auth.t, p: auth.p } }, () => refresh());
  }, [code, auth.t, auth.p, qc, enabled]);
}
